import styles from "../../styles/ReportViewer.module.scss"
import { Checkbox as CheckboxAnt, Col, InputNumber, Radio, RadioChangeEvent, Row, Space, Tooltip } from "antd"
import { DateTime } from "luxon"
import { FC, useEffect, useState } from "react"
import { DateInput, TextInput } from "../../../../components/controls/inputs"
import PluginGridSelectorInput from "../../../../components/controls/PluginGridSelectorInput"
import { IDatePeriod, MaxDate, MinDate } from "../../../../components/datePeriodPicker/datePeriodPicker"
import BaseDocumentCreator from "../../../../components/documents/baseDocumentCreator"
import { IOption, Select } from "../../../../components/selects/select"
import { DatePeriodPanel } from "../../../../hoc/DatePeriodPanel/DatePeriodPanel"
import { IEntitySimpleDTO } from "../../../../libs/coreapi-dto/@types/common"
import { useAppContext } from "../../../../system/providers/appContextProvider"
import { useUserContext } from "../../../../system/providers/userContextProvider"
import { ReportFilterDataType } from "../../Shared/Enums/ReportFilterDataType"
import { IFilter, ReportFilter } from "../../Shared/ReportFilter"
import { ReportFilterGridPluginSettings } from "./ReportFilterGridPluginSettings"
import ReportFilterStyle from './Styles/ReportFilterCreator.module.scss'
import { GridSettings } from "../../../../components/selects/gridSettings"
import { v4 as uuidv4 } from 'uuid'
import renderGlobalAlert from "../../../../system/hooks/useGlobalAlert"
import { ReportFilterAdpriceGridPluginSettings } from "./ReportFilterAdpriceGridPluginSettings"
import { ReportsDateTimePeriodPanel } from "../../../../hoc/DatePeriodPanel/ReportsDateTimePeriodPanel"
import moment from "moment"
import { isNullOrWhiteSpace } from "../../Common/CommonFunctions"
import _ from 'underscore'
import { calculateFilterWeights, calculateRows, findRecursive, getRows, refreshFiltersState, ReportFilterRow } from "./ReportFilterStateFunctions"
import { convertDoodleDateToUTCFormattedString } from "./DateTimeStupidConversions"

interface IReportFilterCreator {
    filters: ReportFilter[]
    asmName: string
    userId: string
    visibleFilters: ReportFilter[]
    cancel: () => void
    process: () => void
}

interface IAdprice {
    idGlobal: string,
    adprice: number,
    highAdprice: number,
    lowAdprice: number
}

export const ReportFilterCreator: FC<IReportFilterCreator> = (props) => {
    const appCtx = useAppContext()
    const userCtx = useUserContext()
    const [filters, setFilters] = useState<Array<ReportFilter>>(props.filters)
    const [rows, setRows] = useState<Array<ReportFilterRow>>()
    const defaultDisplaytems: IAdprice = { idGlobal: uuidv4(), adprice: 0, highAdprice: 0, lowAdprice: 0 }
    const [selectedItem, setSelectedItem] = useState<IGridRow | undefined>();

    function reloadArray() {
        let arr: Array<ReportFilter> = filters as Array<ReportFilter>
        let allFilters: Array<IFilter> = []
        findRecursive(arr, allFilters)
        refreshFiltersState(allFilters)
        setFilters([...arr])
    }

    useEffect(() => {
        // componentDidMount events
        calculateFilterWeights(filters)
        calculateRows(filters)
        setRows(getRows(filters))
        reloadArray()
        return () => {
            // componentWillUnmount events
        }
    }, [])

    const getDisplayItems = () => {
        let filter = filters.find(c => c.dataType === ReportFilterDataType.Grid)
        if (!filter) {
            return []
        }
        return filter?.values?.length === 0 ? [defaultDisplaytems] : JSON.parse(filter.values.toString()) as IAdprice[];
    }

    const [displayItems, setDisplayItems] = useState<IAdprice[]>(getDisplayItems());

    useEffect(() => {
        const filter = filters.find(c => c.name === 'adprices')
        if (filter === undefined) return
        filter.values = [JSON.stringify(displayItems)]
    }, [displayItems])

    const deleteRowtems = () => {
        if (displayItems.length !== 1) {
            if (selectedItem?.idGlobal) {
                setDisplayItems([...displayItems.filter(c => c.idGlobal != selectedItem?.idGlobal)])
            }
            else {
                setDisplayItems([...displayItems.filter(c => c.idGlobal !== displayItems[displayItems.length - 1].idGlobal)])
            }
        }
    }

    const validateDisplayAdprice = () => {
        let lastRow = displayItems[displayItems.length - 1]
        if ((lastRow.lowAdprice + lastRow.highAdprice) === 0) {
            renderGlobalAlert({
                variant: "warning",
                title: `Ошибка добавление накопительной системы. Не указан верхний ${lastRow.lowAdprice} и нижний предел ${lastRow.highAdprice}`,
                statusCode: 500,
            });
            return
        }

        if (lastRow.highAdprice < lastRow.lowAdprice) {
            renderGlobalAlert({
                variant: "warning",
                title: `Ошибка добавление накопительной системы. Нижний предел ${lastRow.lowAdprice} не может быть больше верхнего предела ${lastRow.highAdprice}`,
                statusCode: 500,
            });
            return
        }

        let newDisplay = {
            idGlobal: uuidv4(),
            lowAdprice: Number((lastRow.highAdprice + 0.01).toFixed(2)),
            highAdprice: Number((lastRow.highAdprice + 0.02).toFixed(2)),
            adprice: Number((lastRow.adprice + 0.01).toFixed(2))
        } as IAdprice

        setDisplayItems([...displayItems, newDisplay])
    }

    function getSelector(filter: ReportFilter, multi: boolean) {
        return (
            <div>
                <Tooltip title={filter.hint}>
                    <PluginGridSelectorInput
                        label={filter.caption}
                        mnemocode={filter.pluginCode as IPlugin}
                        multipleSelect={multi}
                        disabled={filter.disabled}
                        selectedIds={filter.values}
                        displayNames={filter.displayNames}
                        gridPlugin={ReportFilterGridPluginSettings}
                        onClear={() => {
                            filter.values.length = 0
                            filter.displayNames.length = 0
                            reloadArray()
                        }}
                        onSelect={(option: Array<IEntitySimpleDTO>) => {
                            filter.values.length = 0
                            filter.displayNames.length = 0
                            option.forEach(item => {
                                filter.values.push(item.idGlobal)
                                filter.displayNames?.push(item.displayName)
                            })
                            reloadArray()
                        }}
                    />
                </Tooltip>
            </div>
        )
    }

    //Конвертор пляшущих дат, в будущем, когда поймут, что нужно делать нормально сразу - нужно будет переделать
    const onDatePeriodChange = (period: IDatePeriod, dateStrings: [string, string], filter: ReportFilter) => {
        filter.values.length = 0

        //Берётся лямая дата и время неизвестного характера, которую пользователь видит глазами на экране в ячейках календарей
        //И на основе её формируется дата для временной зоны пользователя, прислюнявив к ней как есть время от лямой даты
        //А затем преобразовав в UTC вменяемая дата пойдёт на сервер

        if (period != undefined) {
            const formattedDateFrom: string = convertDoodleDateToUTCFormattedString(dateStrings[0], userCtx)
            const formattedDateTo: string = convertDoodleDateToUTCFormattedString(dateStrings[1], userCtx)

            filter.values.push(formattedDateFrom)
            filter.values.push(formattedDateTo)
        }
        reloadArray()
    }

    const onDateChange = (dateString: string | undefined, filter: ReportFilter) => {
        filter.values.length = 0

        if (dateString) {
            const formattedDate: string = convertDoodleDateToUTCFormattedString(dateString, userCtx)
            filter.values.push(formattedDate)
        }
        reloadArray()
    }

    const checkListAdv = (filter: ReportFilter) => {
        return (
            <div>
                <div dangerouslySetInnerHTML={{ __html: filter.caption }} />
                {/* Устарелое */}
                {
                    filter.defaultValues.map((fi) => {
                        const isChecked = filter.values.some(s => s === fi.value)
                        return (
                            <div>
                                <Tooltip title={fi.hint}>
                                    <CheckboxAnt onChange={(e) => {
                                        if (e.target.checked) {
                                            if (!isChecked) {
                                                filter.values.push(fi.value)
                                                fi.checked = true
                                                reloadArray()
                                            }
                                        } else {
                                            if (isChecked) {
                                                var index = filter.values.indexOf(fi.value)
                                                filter.values.splice(index, 1)
                                                fi.checked = false
                                                reloadArray()
                                            }
                                        }
                                    }}
                                        checked={isChecked}
                                        disabled={fi.disabled}
                                    >{fi.displayName}</CheckboxAnt>
                                </Tooltip>
                            </div>
                        )
                    })
                }
                {
                    filter.subFilters.map((f) => {
                        let isChecked: boolean
                        if (f.values.length > 0) {
                            try {
                                isChecked = JSON.parse(f.values[0].toLowerCase())
                            } catch {
                                isChecked = false
                            }
                        }
                        else {
                            isChecked = false
                        }
                        
                        if (!isChecked) {
                            var index = filter.values.indexOf(f.name)
                            filter.values.splice(index, 1)
                        } else {
                            filter.values.push(f.name)
                        }

                        return (
                            <div>
                                <Tooltip title={f.hint}>
                                    <CheckboxAnt onChange={(e) => {
                                        if (e.target.checked) {
                                            if (!isChecked) {
                                                filter.values.push(f.name)
                                                f.values.length = 0
                                                f.values.push(String(true))
                                                reloadArray()
                                            }
                                        } else {
                                            if (isChecked) {
                                                var index = filter.values.indexOf(f.name)
                                                filter.values.splice(index, 1)
                                                f.values.length = 0
                                                f.values.push(String(false))
                                                reloadArray()
                                            }
                                        }
                                    }}
                                        checked={isChecked}
                                        disabled={f.disabled}
                                    >{f.caption}</CheckboxAnt>
                                </Tooltip>
                            </div>
                        )
                    })
                }

            </div>
        )
    }

    const getFilters = (filter: ReportFilter) => {
        switch (filter.dataType) {
            case ReportFilterDataType.SinglePlugin:
                return (
                    <div className={ReportFilterStyle.wrapperFilter}>
                        {getSelector(filter, false)}
                    </div>
                )
            case ReportFilterDataType.MultiPlugin:
                return (
                    <div className={ReportFilterStyle.wrapperFilter}>
                        {getSelector(filter, true)}
                    </div>
                )
            case ReportFilterDataType.Boolean:
                let isChecked: boolean
                if (filter.values.length > 0) {
                    isChecked = JSON.parse(filter.values[0].toLowerCase())
                }
                else {
                    isChecked = false
                    filter.values.push(String(isChecked))
                }
                return (
                    <Tooltip title={filter.hint}>
                        <CheckboxAnt
                            onChange={(e) => {
                                filter.values.length = 0
                                filter.values.push(String(e.target.checked))
                                reloadArray()
                            }}
                            checked={isChecked}
                            disabled={filter.disabled}
                        >
                            {filter.caption}
                        </CheckboxAnt>
                    </Tooltip>
                )
            case ReportFilterDataType.CheckList:
            case ReportFilterDataType.CheckListAdv:
                return (
                    <>
                        {checkListAdv(filter)}
                    </>
                )
            case ReportFilterDataType.Date:
                const date = DateTime.fromISO(moment.utc(moment(filter.values[0])).tz(userCtx.userTimeZoneId).toISOString())
                return (
                    <div>
                        <Tooltip title={filter.hint}>
                            <DateInput
                                label={filter.caption}
                                inputId={filter.name}
                                className={styles.field_Date}
                                value={date}
                                disabled={filter.disabled}
                                onBlur={(value, dateString) => {
                                    onDateChange(dateString, filter)
                                }}
                            />
                        </Tooltip>
                    </div>
                )
            case ReportFilterDataType.Period:
            case ReportFilterDataType.TimePeriod:
                let dateFrom: DateTime, dateTo: DateTime

                if (filter.values.length == 2) {
                    dateFrom = DateTime.fromISO(filter.values[0])
                    dateTo = DateTime.fromISO(filter.values[1])
                }
                else {
                    dateFrom = MinDate
                    dateTo = MaxDate
                }

                dateFrom = DateTime.fromISO(moment.utc(moment(dateFrom.toISO())).tz(userCtx.userTimeZoneId).toISOString())
                dateTo = DateTime.fromISO(moment.utc(moment(dateTo.toISO())).tz(userCtx.userTimeZoneId).toISOString())

                const period: IDatePeriod = { startDate: dateFrom, endDate: dateTo } as IDatePeriod

                return (
                    <div>
                        <Tooltip title={filter.hint}>
                            {
                                filter.dataType === ReportFilterDataType.Period &&
                                <DatePeriodPanel
                                    label={filter.caption}
                                    datePeriod={period}
                                    disabled={filter.disabled}
                                    onDatePeriodChange={(period, dateStrings) => { onDatePeriodChange(period, dateStrings, filter) }}
                                />
                            }
                            {
                                filter.dataType === ReportFilterDataType.TimePeriod &&
                                <ReportsDateTimePeriodPanel
                                    label={filter.caption}
                                    datePeriod={period}
                                    disabled={filter.disabled}
                                    showTime={filter.dataType === ReportFilterDataType.TimePeriod}
                                    onDatePeriodChange={(period, dateStrings) => { onDatePeriodChange(period, dateStrings, filter) }}
                                />
                            }
                        </Tooltip>
                    </div>
                )
            case ReportFilterDataType.Integer:
                const val = filter.values.length > 0 ? filter.values[0] : 0
                return (
                    <div>
                        <div dangerouslySetInnerHTML={{ __html: filter.caption }} />
                        <Tooltip title={filter.hint}>
                            <InputNumber
                                value={val}
                                disabled={filter.disabled}
                                onChange={(e) => {
                                    if (e) {
                                        filter.values[0] = e.toString()
                                        reloadArray()
                                    }
                                }} />
                        </Tooltip>
                    </div>
                )
            case ReportFilterDataType.Float:
                const fval = filter.values.length > 0 ? filter.values[0] : "0"
                return (
                    <div>
                        <div dangerouslySetInnerHTML={{ __html: filter.caption }} />
                        <Tooltip title={filter.hint}>
                            <InputNumber<string>
                                value={fval}
                                disabled={filter.disabled}
                                step="0.01"
                                onChange={(e) => {
                                    if (e) {
                                        filter.values[0] = e.toString()
                                        reloadArray()
                                    }
                                }}
                                stringMode
                            />
                        </Tooltip>
                    </div>
                )
            case ReportFilterDataType.Combo:
                let arr: Array<IOption> = new Array<IOption>()
                if (filter.subFilters.length > 0) {
                    filter.subFilters.forEach(item => {
                        const op: IOption = { displayName: item.caption, value: item.name }
                        arr.push(op)
                    })
                } else {
                    filter.defaultValues.forEach(item => {
                        const op: IOption = { displayName: item.displayName, value: item.value }
                        arr.push(op)
                    })
                }

                let selected: IOption = arr[0]
                if (filter.values.length > 0) {
                    const item: string = filter.values[0]
                    //Проверить, может такого фильтра уже и нет
                    if (filter.values.some(s => s === item)) {
                        selected = { displayName: item, value: item }
                    }
                } else {
                    filter.values.push(selected.value)
                }
                return (
                    <div>
                        <div dangerouslySetInnerHTML={{ __html: filter.caption }} />
                        <div>
                            <Tooltip title={filter.hint}>
                                <Select
                                    onSelect={(option) => {
                                        filter.values.length = 0
                                        filter.values.push(option.value)
                                        filter.subFilters.forEach(u => {
                                            u.values.length = 0
                                            u.values.push(String(u.name === option.value))
                                        })
                                        reloadArray()
                                    }}
                                    defaultOption={selected}
                                    value={selected}
                                    disabled={filter.disabled}
                                    options={arr}>
                                </Select>
                            </Tooltip>
                        </div>
                    </div>
                )
            case ReportFilterDataType.Label:
                return (
                    <div>
                        <Tooltip title={filter.hint}>
                            <div dangerouslySetInnerHTML={{ __html: filter.caption }} />
                        </Tooltip>
                    </div>
                )
            case ReportFilterDataType.String:
                let txt: string
                if (filter.values.length > 0) {
                    txt = filter.values[0]
                } else {
                    txt = ""
                }
                return (
                    <div>
                        <div dangerouslySetInnerHTML={{ __html: filter.caption }} />
                        <Tooltip title={filter.hint}>
                            <TextInput
                                inputId={filter.name}
                                inline={true}
                                value={txt}
                                onChange={(value) => {
                                    filter.values.length = 0
                                    if (!isNullOrWhiteSpace(value)) {
                                        filter.values.push(value.trim())
                                    }
                                    reloadArray()
                                }}
                            />
                        </Tooltip>
                    </div>
                )
            case ReportFilterDataType.Grid:
                return (
                    <div>
                        <div dangerouslySetInnerHTML={{ __html: filter.caption }} />
                        <Tooltip title={filter.hint}>
                            <div>
                                <GridSettings plugin={ReportFilterAdpriceGridPluginSettings}
                                    onChangeData={(value) => {
                                        const datas = displayItems.map((x) => (x.idGlobal === value.idGlobal ? {
                                            ...x,
                                            lowAdprice: value.propertyName === 'lowAdprice' ? value.value as number : x.lowAdprice as number,
                                            highAdprice: value.propertyName === 'highAdprice' ? value.value as number : x.highAdprice as number,
                                            adprice: value.propertyName === 'adprice' ? value.value as number : x.adprice as number,
                                            idGlobal: x.idGlobal
                                        }
                                            : x)
                                        )
                                        setDisplayItems(datas);
                                    }}
                                    onDelete={() => deleteRowtems()}
                                    onValidate={() => validateDisplayAdprice()}
                                    displayItems={displayItems}
                                    setDisplayItems={() => setDisplayItems([...displayItems, defaultDisplaytems])}
                                    selectedItem={selectedItem}
                                    setSelectedItem={(value) => setSelectedItem(value)}
                                // disabled={filter.disabled} ToDo
                                >
                                </GridSettings>
                            </div>
                        </Tooltip>
                    </div>
                )
            case ReportFilterDataType.RadioGroup:
                return (
                    <div>
                        <div dangerouslySetInnerHTML={{ __html: filter.caption }} />
                        <Radio.Group
                            style={{ fontSize: 'unset' }}
                            value={
                                filter.subFilters.length > 0 ? (_.find(filter.subFilters, function (f: ReportFilter) {
                                    let isChecked: boolean
                                    if (f.values.length > 0) {
                                        try {
                                            isChecked = JSON.parse(f.values[0].toLowerCase())
                                        } catch {
                                            isChecked = false 
                                        }
                                        return isChecked
                                    }
                                    else {
                                        return false
                                    }
                                }))?.name : filter.values[0]
                            }
                            disabled={filter.disabled}
                            onChange={(e: RadioChangeEvent) => {
                                filter.values.length = 0
                                filter.values.push(e.target.value)
                                filter.subFilters.forEach(u => {
                                    u.values.length = 0
                                    u.values.push(String(u.name === e.target.value))
                                })
                                reloadArray()
                            }}
                        >
                            <Space direction={filter.isHorizontal ? "horizontal" : "vertical"}>
                                {
                                    filter.defaultValues.map((subFilter) => {
                                        const isRadioChecked = filter.values.some(s => s === subFilter.value)
                                        const disabled = !isRadioChecked
                                        subFilter.filters?.map((subSubFilter) => { subSubFilter.disabled = disabled })
                                        return (<Tooltip title={subFilter.hint}>
                                            <Radio value={subFilter.value}>{subFilter.displayName}</Radio>
                                            {subFilter.filters &&
                                                <div className={styles.wrapper}>
                                                    {
                                                        subFilter.filters.map((sfilter) => {
                                                            return getFilters(sfilter)
                                                        })
                                                    }
                                                </div>
                                            }
                                        </Tooltip>
                                        )
                                    })
                                }
                                {
                                    filter.subFilters.map((subFilter) => {
                                        let isChecked: boolean
                                        if (subFilter.values.length > 0) {
                                            try {
                                            isChecked = JSON.parse(subFilter.values[0].toLowerCase())
                                            } catch {
                                                isChecked = false    
                                            }
                                        }
                                        else {
                                            isChecked = false
                                        }
                                        subFilter.disabled = filter.disabled
                                        const disabled = !isChecked && !subFilter.disabled
                                        return (<Tooltip title={subFilter.hint}>
                                            <Radio
                                                value={subFilter.name}
                                                disabled={filter.disabled}
                                                >
                                                {subFilter.caption}
                                            </Radio>

                                            {subFilter.subFilters.length > 0 &&
                                                <div className={styles.wrapper}>
                                                    {
                                                        subFilter.subFilters.map((sfilter) => {
                                                            sfilter.disabled = disabled
                                                            return getFilters(sfilter)
                                                        })
                                                    }
                                                </div>
                                            }
                                        </Tooltip>
                                        )
                                    })
                                }
                            </Space>
                        </Radio.Group>
                    </div>
                )
            case ReportFilterDataType.GroupBox:
               
                return (
                    <div>
                        <div dangerouslySetInnerHTML={{ __html: filter.caption }} />
                        {filter.isHorizontal && filter.subFilters &&
                            <Row wrap={false} justify="start">
                                {
                                    filter.subFilters.map((sfilter) => {
                                        const span = sfilter.colSpan?.toString()
                                        return (
                                            <Col flex={span} >
                                                <div style={{ padding: 10 }}>
                                                    {getFilters(sfilter)}
                                                </div>
                                            </Col>
                                        )
                                    })
                                }
                            </Row>
                        }
                        {!filter.isHorizontal && filter.subFilters &&
                            <>
                                {
                                    filter.subFilters.map((sfilter) => {
                                        const span = sfilter.colSpan?.toString()
                                        return (
                                            <div style={{ padding: 10 }}>
                                                {getFilters(sfilter)}
                                            </div>
                                        )
                                    })
                                }
                            </>
                        }
                    </div>
                )
            default:
                return (<></>)
        }
    }
    return (
        <BaseDocumentCreator
            ok={{
                onClick: () => {
                    const encodedReportAssName = encodeURIComponent(props.asmName)
                    appCtx.reportAPIService?.reportRequest.then(r => r.saveSettings(userCtx.idGlobal, encodedReportAssName, props.filters))//ToDo siv - градусник и понять что из сеттнгс сохр возможно только для отчётов, а не ПФ
                    props.process()
                }
            }}
            cancel={{ onClick: () => { props.cancel() } }}
        >
            {
                rows?.map((row) => {
                    return (
                        <Row wrap={false} justify="start">
                            {
                                row.filters.map((filter) => {
                                    const span = filter.dataType === ReportFilterDataType.Period ? undefined : filter.colSpan?.toString();
                                    return (
                                        <Col flex={span} >
                                            <div style={{ padding: 10 }}>
                                                {getFilters(filter)}
                                            </div>
                                        </Col>
                                    )
                                })
                            }
                        </Row>
                    )
                })
            }
        </BaseDocumentCreator>
    )
}